import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, AuthResponse } from '../types';
import { authApi } from '../api/client';

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    email: string;
    password: string;
    fullName: string;
    tenantSlug: string;
  }) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser]       = useState<User | null>(null);
  const [token, setToken]     = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on app start
  useEffect(() => {
    const savedToken = localStorage.getItem('edunex_token');
    const savedUser  = localStorage.getItem('edunex_user');

    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authApi.login(email, password);
    const data: AuthResponse = response.data;

    // Save to localStorage
    localStorage.setItem('edunex_token', data.accessToken);

    const userData: User = {
      userId:     data.userId,
      email:      data.email,
      fullName:   data.fullName,
      role:       data.role as User['role'],
      tenantSlug: data.tenantSlug,
    };
    localStorage.setItem('edunex_user', JSON.stringify(userData));

    setToken(data.accessToken);
    setUser(userData);
  };

  const register = async (formData: {
    email: string;
    password: string;
    fullName: string;
    tenantSlug: string;
  }) => {
    const response = await authApi.register(formData);
    const data: AuthResponse = response.data;

    localStorage.setItem('edunex_token', data.accessToken);

    const userData: User = {
      userId:     data.userId,
      email:      data.email,
      fullName:   data.fullName,
      role:       data.role as User['role'],
      tenantSlug: data.tenantSlug,
    };
    localStorage.setItem('edunex_user', JSON.stringify(userData));

    setToken(data.accessToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('edunex_token');
    localStorage.removeItem('edunex_user');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, token, login, register, logout, isLoading
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook — use this in every component
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}